import { initializePaddle, Paddle } from '@paddle/paddle-js';
import api from './api';
import { BillingConfig, BillingStatus } from '../types/billing';

export const billingService = {
  /** Public Paddle config (client token + price id) from the backend. */
  async getConfig(): Promise<BillingConfig> {
    const response = await api.get<BillingConfig>('/billing/config');
    return response.data;
  },

  /** The current user's subscription/entitlement status. */
  async getSubscription(): Promise<BillingStatus> {
    const response = await api.get<BillingStatus>('/billing/subscription');
    return response.data;
  },

  /** Cancel at the end of the current period (keeps Pro until then). */
  async cancelSubscription(): Promise<{ message: string; effectiveAt: string | null }> {
    const response = await api.post<{ message: string; effectiveAt: string | null }>(
      '/billing/cancel'
    );
    return response.data;
  },

  /** Get a Paddle customer-portal URL (manage payment method, invoices). */
  async getPortalUrl(): Promise<string> {
    const response = await api.get<{ url: string }>('/billing/portal');
    return response.data.url;
  },
};

// Cache the initialized Paddle instance so we only bootstrap Paddle.js once.
let paddlePromise: Promise<Paddle | undefined> | null = null;

async function getPaddle(config: BillingConfig): Promise<Paddle | undefined> {
  if (!config.clientToken) {
    throw new Error('Paddle is not configured');
  }
  if (!paddlePromise) {
    paddlePromise = initializePaddle({
      environment: config.environment === 'production' ? 'production' : 'sandbox',
      token: config.clientToken,
      // Broadcast a window event when checkout completes so the Billing page can
      // refresh entitlement (which the webhook grants a moment later) without a
      // manual reload.
      eventCallback: (event) => {
        if (event?.name === 'checkout.completed') {
          window.dispatchEvent(new CustomEvent('paddle:checkout-completed'));
        }
      },
    });
  }
  return paddlePromise;
}

/**
 * Open Paddle's overlay checkout for the configured price.
 *
 * The logged-in user's email links the checkout to their account, and
 * `customData.user_id` is what the backend webhook reads to attach the
 * resulting subscription to the right user.
 */
export type BillingCycle = 'monthly' | 'annual';

export async function openCheckout(opts: {
  config: BillingConfig;
  email: string;
  userId: string;
  /** Defaults to monthly, so every existing caller keeps its behaviour. */
  cycle?: BillingCycle;
}): Promise<void> {
  const { config, email, userId, cycle = 'monthly' } = opts;

  // Resolve the price here rather than at the call site: a caller that asks for
  // annual when none is configured must not silently fall back to charging
  // monthly — that would take a payment for a plan the user did not choose.
  const priceId = cycle === 'annual' ? config.priceIdAnnual : config.priceId;
  if (!priceId) {
    throw new Error(
      cycle === 'annual'
        ? 'Annual billing is not available'
        : 'No Paddle price configured',
    );
  }
  const paddle = await getPaddle(config);
  if (!paddle) {
    throw new Error('Failed to initialize Paddle');
  }
  paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    customer: { email },
    customData: { user_id: userId },
  });
}
